import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import { THEMES } from "@starquest/core";
import ThemeWrapper from "../components/ThemeWrapper";
import styles from "./ThemePicker.module.css";

// All selectable themes — base is always available (no approval needed)
const SELECTABLE = ["explorer", "wizard", "sports", "starwars", "disney"] as const;

export default function ThemePicker() {
  const navigate = useNavigate();
  const { familyId: authFamilyId } = useAuthStore();
  const { selectedKid, updateKid } = useFamilyStore();

  // PIN kids have authFamilyId = null — read from stored family data
  const storedFamilyId = (() => {
    try { return JSON.parse(localStorage.getItem("sq_last_family") ?? "null")?.familyId ?? null; }
    catch { return null; }
  })();
  const familyId = authFamilyId || selectedKid?.familyId || storedFamilyId || null;
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [error,      setError]      = useState("");

  useEffect(() => {
    if (!selectedKid) navigate("/kid");
  }, [selectedKid, navigate]);

  if (!selectedKid) return null;

  const activeTheme  = selectedKid.theme ?? "base";
  const pendingTheme = selectedKid.themeRequest?.themeId;

  async function pick(themeId: string) {
    if (!familyId || submitting) return;
    setSubmitting(true); setError("");
    try {
      const now = new Date().toISOString();
      await api.requestTheme(familyId, selectedKid!.kidId, themeId);
      updateKid(selectedKid!.kidId, { themeRequest: { themeId, requestedAt: now } });
      setSuccess(true);
      setTimeout(() => navigate("/kid"), 1800);
    } catch { setError("Failed to send request. Try again."); }
    finally { setSubmitting(false); }
  }

  async function resetToDefault() {
    if (!familyId || submitting) return;
    setSubmitting(true); setError("");
    try {
      // Backend applies base theme immediately and cancels any pending request
      await api.requestTheme(familyId, selectedKid!.kidId, "base");
      // Update local state: clear theme to base, cancel pending request
      updateKid(selectedKid!.kidId, { theme: "base" as any, themeRequest: undefined });
      setSuccess(true);
      setTimeout(() => navigate("/kid"), 1000);
    } catch { setError("Failed to reset. Try again."); }
    finally { setSubmitting(false); }
  }

  return (
    <ThemeWrapper kid={selectedKid}>
      <div className={styles.page}>
        <div className={styles.title}>Choose Your Theme 🎨</div>
        <div className={styles.sub}>
          Pick a theme — {selectedKid.name}'s {THEMES[activeTheme]?.parentRole ?? "parent"} will approve it
        </div>

        {/* Current theme indicator */}
        {activeTheme !== "base" && (
          <div className={styles.currentBadge}>
            {THEMES[activeTheme]?.emoji} Currently using: <strong>{THEMES[activeTheme]?.name}</strong>
          </div>
        )}

        {pendingTheme && !success && (
          <div className={styles.pending}>
            ⏳ "{THEMES[pendingTheme]?.name}" request is pending approval
          </div>
        )}

        <div className={styles.grid}>
          {SELECTABLE.map((id) => {
            const t = THEMES[id];
            const isActive  = activeTheme === id;
            const isPending = pendingTheme === id && !isActive;
            return (
              <button
                key={id}
                className={`${styles.card} ${isActive ? styles.cardActive : ""} ${isPending ? styles.cardPending : ""}`}
                onClick={() => pick(id)}
                disabled={submitting || isActive}
              >
                <div className={styles.emoji}>{t.emoji}</div>
                <div className={styles.name}>{t.name}</div>
                <div className={styles.labels}>{t.challengeName} · {t.pointName}</div>
                {isActive  && <div className={styles.badge}>✓ Active</div>}
                {isPending && <div className={styles.badge}>⏳ Pending</div>}
              </button>
            );
          })}
        </div>

        {/* Default/reset option — always available, no approval */}
        {activeTheme !== "base" && (
          <button
            className={styles.resetBtn}
            onClick={resetToDefault}
            disabled={submitting}
          >
            🔄 Reset to Default Theme
          </button>
        )}
        {activeTheme === "base" && (
          <div className={styles.usingDefault}>
            ✓ Using the default theme — pick one above to personalise!
          </div>
        )}

        {success && <div className={styles.success}>✅ {activeTheme === "base" ? "Reset to default!" : "Request sent! Awaiting approval."}</div>}
        {error   && <div style={{ color: "var(--coral)", fontSize: 13, textAlign: "center" }}>{error}</div>}

        <button className={styles.back} onClick={() => navigate("/kid")}>← Back</button>
      </div>
    </ThemeWrapper>
  );
}
